import React, { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { ShoppingCartProvider } from "@/hooks/useShoppingCart";
import { useInactivityLogout } from "@/hooks/useInactivityLogout";
import { InactivityLogoutProvider } from "@/components/InactivityLogoutProvider";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import CartSidebar from "@/components/shop/CartSidebar";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { useCommentReactionNotifications } from "@/hooks/useCommentReactionNotifications";
import { usePageViewTracking } from "@/hooks/usePageViewTracking";
import LazyLoadingFallback from "@/components/LazyLoadingFallback";

// Critical routes - loaded eagerly for fast initial paint
import Home from "./pages/Home";
import Tutors from "./pages/Tutors";
import Auth from "./pages/Auth";
import TutorDetails from "./pages/TutorDetails";
import NotFound from "./pages/NotFound";

// Core components needed immediately
import ProtectedRoute from "./components/ProtectedRoute";
import ProtectedTutorRoute from "./components/ProtectedTutorRoute";
import { UnifiedConsentGuard } from "./components/UnifiedConsentGuard";
import DashboardLayout from "./layouts/DashboardLayout";
import ProfileRouter from "./components/ProfileRouter";
import LiveChatWidget from "./components/LiveChatWidget";
import { SidebarProvider } from "@/components/ui/sidebar";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import FrozenTutorGuard from "./components/FrozenTutorGuard";
import RedirectHandler from "./components/RedirectHandler";

// Lazy-loaded pages - reduces initial bundle significantly
const SavedTutors = lazy(() => import("./pages/SavedTutors"));
const InspirePosts = lazy(() => import("./pages/InspirePosts"));
const ExploreLessons = lazy(() => import("./pages/ExploreLessons"));
const Classroom = lazy(() => import("./pages/Classroom"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminPaymentDashboard = lazy(() => import("./pages/AdminPaymentDashboard"));
const TutorRequests = lazy(() => import("./pages/TutorRequests"));
const SubjectRequests = lazy(() => import("./pages/SubjectRequests"));
const AdminSubjects = lazy(() => import("./pages/AdminSubjects"));
const TutorProfile = lazy(() => import("./pages/TutorProfile"));
const StudentProfile = lazy(() => import("./pages/StudentProfile"));
const Schedule = lazy(() => import("./pages/Schedule"));
const Students = lazy(() => import("./pages/Students"));
const Lessons = lazy(() => import("./pages/Lessons"));
const TutorLessons = lazy(() => import("./pages/TutorLessons"));
const LessonDetail = lazy(() => import("./pages/LessonDetail"));
const LessonEdit = lazy(() => import("./pages/LessonEdit"));
const RescheduleApproval = lazy(() => import("./pages/RescheduleApproval"));
const Messages = lazy(() => import("./pages/Messages"));
const FilePreview = lazy(() => import("./pages/FilePreview"));
const Earnings = lazy(() => import("./pages/Earnings"));
const AppSettings = lazy(() => import("./pages/AppSettings"));
const BookingConfirmation = lazy(() => import("./pages/BookingConfirmation"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const Subjects = lazy(() => import("./pages/Subjects"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const EnglishSubject = lazy(() => import("./pages/EnglishSubject"));
const FitnessSubject = lazy(() => import("./pages/FitnessSubject"));
const ProgrammingSubject = lazy(() => import("./pages/ProgrammingSubject"));
const TravelSubject = lazy(() => import("./pages/TravelSubject"));
const MusicSubject = lazy(() => import("./pages/MusicSubject"));
const SubjectVideos = lazy(() => import("./pages/SubjectVideos"));
const FitnessVideos = lazy(() => import("./pages/FitnessVideos"));
const Countries = lazy(() => import("./pages/Countries"));
const DynamicCountryPage = lazy(() => import("./pages/DynamicCountryPage"));
const USCountryPage = lazy(() => import("./pages/USCountryPage"));
const CanadaCountryPage = lazy(() => import("./pages/CanadaCountryPage"));
const UKCountryPage = lazy(() => import("./pages/UKCountryPage"));
const AustraliaCountryPage = lazy(() => import("./pages/AustraliaCountryPage"));
const GermanyCountryPage = lazy(() => import("./pages/GermanyCountryPage"));
const NetherlandsCountryPage = lazy(() => import("./pages/NetherlandsCountryPage"));
const HungaryCountryPage = lazy(() => import("./pages/HungaryCountryPage"));
const EgyptCountryPage = lazy(() => import("./pages/EgyptCountryPage"));
const ItalyCountryPage = lazy(() => import("./pages/ItalyCountryPage"));
const CountryArticlesPage = lazy(() => import("./pages/CountryArticlesPage"));
const TravelStoryForm = lazy(() => import("./pages/TravelStoryForm"));
const VideoCall = lazy(() => import("./pages/VideoCall"));
const LiveChatSupport = lazy(() => import("./pages/LiveChatSupport"));
const LiveChatAgents = lazy(() => import("./pages/LiveChatAgents"));
const MyTutors = lazy(() => import("./pages/MyTutors"));
const AdminAnnouncements = lazy(() => import("./pages/AdminAnnouncements"));
const Announcements = lazy(() => import("./pages/Announcements"));
const Shop = lazy(() => import("./pages/Shop"));
const AdminShop = lazy(() => import("./pages/AdminShop"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const AdminContent = lazy(() => import("./pages/AdminContent"));
const AdminReviewArticles = lazy(() => import("./pages/AdminReviewArticles"));
const AdminDownloadContent = lazy(() => import("./pages/AdminDownloadContent"));
const AdminFinancials = lazy(() => import("./pages/AdminFinancials"));
const AdminAssistantPayments = lazy(() => import("./pages/AdminAssistantPayments"));
const AdminFeaturedTutors = lazy(() => import("./pages/AdminFeaturedTutors"));
const SupportMessages = lazy(() => import("./pages/SupportMessages"));
const AdminTutors = lazy(() => import("./pages/AdminTutors"));
const AdminStudents = lazy(() => import("./pages/AdminStudents"));
const MyTravelStories = lazy(() => import("./pages/MyTravelStories"));
const TravelStories = lazy(() => import("./pages/TravelStories"));
const AdminTravelStories = lazy(() => import("./pages/AdminTravelStories"));
const ManageEmailNotifications = lazy(() => import("./pages/admin/ManageEmailNotifications"));
const AdminTravelStoriesOrder = lazy(() => import("./pages/AdminTravelStoriesOrder"));
const AdminInspirePostsOrder = lazy(() => import("./pages/AdminInspirePostsOrder"));
const AdminFocusPages = lazy(() => import("./pages/AdminFocusPages"));
const AdminTravelPages = lazy(() => import("./pages/AdminTravelPages"));
const AdminTravelManagement = lazy(() => import("./pages/AdminTravelManagement"));
const HeroGuideSettings = lazy(() => import("@/pages/HeroGuideSettings"));
const TutorChats = lazy(() => import("./pages/TutorChats"));
const OnlineTutors = lazy(() => import("@/pages/OnlineTutors"));
const AdminSupportContacts = lazy(() => import("./pages/AdminSupportContacts"));
const OnlineFitnessTutors = lazy(() => import("@/pages/OnlineFitnessTutors"));
const AdminBookings = lazy(() => import("./pages/AdminBookings"));
const AdminLessons = lazy(() => import("./pages/AdminLessons"));
const AdminSubjectManagement = lazy(() => import("./pages/AdminSubjectManagement"));
const AdminUserManagement = lazy(() => import("./pages/AdminUserManagement"));
const AdminUserRecords = lazy(() => import("./pages/AdminUserRecords"));
const AdminProfileSuspensions = lazy(() => import("./pages/AdminProfileSuspensions"));
const AdminFixTutorDisplay = lazy(() => import("./pages/AdminFixTutorDisplay"));
const AdminLocalCoaches = lazy(() => import("./pages/AdminLocalCoaches"));
const BecomeLocalFitnessGuide = lazy(() => import("./pages/BecomeLocalFitnessGuide"));
const BecomeLocalMusicGuide = lazy(() => import("./pages/BecomeLocalMusicGuide"));
const BecomeLocalTravelGuide = lazy(() => import("./pages/BecomeLocalTravelGuide"));
const BecomeLocalTutor = lazy(() => import("./pages/BecomeLocalTutor"));
const LocalFitnessCoaches = lazy(() => import("./pages/LocalFitnessCoaches"));
const LocalTravelGuides = lazy(() => import("./pages/LocalTravelGuides"));
const LocalMusicCoaches = lazy(() => import("./pages/LocalMusicCoaches"));
const LocalCoachDetails = lazy(() => import("./pages/LocalCoachDetails"));
const Chat = lazy(() => import("./pages/Chat"));
const NotificationTest = lazy(() => import("./pages/NotificationTest"));
const EnglishLessons = lazy(() => import("./pages/EnglishLessons"));
const FitnessLessons = lazy(() => import("./pages/FitnessLessons"));
const ProgrammingLessons = lazy(() => import("./pages/ProgrammingLessons"));
const TravelLessons = lazy(() => import("./pages/TravelLessons"));
const MusicLessons = lazy(() => import("./pages/MusicLessons"));
const AdminTerms = lazy(() => import("./pages/AdminTerms"));
const AdminPaymentSettings = lazy(() => import("./pages/AdminPaymentSettings"));
const AdminPaymentHistory = lazy(() => import("./pages/AdminPaymentHistory"));
const AdminEarningsManagement = lazy(() => import("./pages/AdminEarningsManagement"));
const AdminPaymentRequests = lazy(() => import("./pages/AdminPaymentRequests"));
const PaymentRequests = lazy(() => import("./pages/PaymentRequests"));
const PaymentRequestSuccess = lazy(() => import("./pages/PaymentRequestSuccess"));
const AdminHeroImage = lazy(() => import("./pages/AdminHeroImage"));
const AdminHeroTutorCards = lazy(() => import("./pages/AdminHeroTutorCards"));
const HomepageSectionAdmin = lazy(() => import("./pages/admin/HomepageSectionAdmin"));
const AdminTesting = lazy(() => import("./pages/AdminTesting"));
const AdminTutorEvaluation = lazy(() => import("./pages/AdminTutorEvaluation"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const Careers = lazy(() => import("./pages/Careers"));
const Blog = lazy(() => import("./pages/Blog"));
const Press = lazy(() => import("./pages/Press"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
const Safety = lazy(() => import("./pages/Safety"));
const CommunityGuidelines = lazy(() => import("./pages/CommunityGuidelines"));
const ChatEnhancementsContent = lazy(() => import("./components/admin/ChatEnhancementsContent"));
const LanguageAdventure = lazy(() => import("./pages/LanguageAdventure"));
const EgyptMarketingVideoPage = lazy(() => import("./pages/EgyptMarketingVideoPage"));
const TutorNestaPromoPage = lazy(() => import("./pages/TutorNestaPromoPage"));
const ArticleDetailPage = lazy(() => import("./pages/ArticleDetailPage"));
const SuccessStories = lazy(() => import("./pages/SuccessStories"));
const AssistantDashboard = lazy(() => import("./pages/AssistantDashboard"));
const AccountantDashboard = lazy(() => import("./pages/AccountantDashboard"));
const Articles = lazy(() => import("./pages/Articles"));
const CreateArticle = lazy(() => import("./pages/CreateArticle"));
const ArticleDetail = lazy(() => import("./pages/ArticleDetail"));
const Gallery = lazy(() => import("./pages/Gallery"));
const GalleryShowcase = lazy(() => import("./pages/admin/GalleryShowcase"));
const TrackingEvents = lazy(() => import("./pages/admin/TrackingEvents"));
const TutorSuspensionManager = lazy(() => import("./pages/admin/TutorSuspensionManager"));
const AdminDiscounts = lazy(() => import("./pages/admin/Discounts"));
const AdminDiscountCodes = lazy(() => import("./pages/admin/DiscountCodes"));
const AdminGalleryManagement = lazy(() => import("./pages/AdminGalleryManagement"));
const TutorVideosPage = lazy(() => import("./pages/TutorVideosPage"));
const InstallApp = lazy(() => import("./pages/InstallApp"));
const CoursesPage = lazy(() => import("./pages/CoursesPage"));
const CourseDetailsPage = lazy(() => import("./pages/CourseDetailsPage"));
const SubscriptionPage = lazy(() => import("./pages/SubscriptionPage"));
const CoursePurchaseSuccessPage = lazy(() => import("./pages/CoursePurchaseSuccessPage"));
const AdminCourses = lazy(() => import("./pages/AdminCourses"));
const AdminCourseForm = lazy(() => import("./pages/AdminCourseForm"));
const MyCoursesPage = lazy(() => import("./pages/MyCoursesPage"));
const ManageSubscriptionPage = lazy(() => import("./pages/ManageSubscriptionPage"));
const AdminEmail = lazy(() => import("./pages/AdminEmail"));
const TasksManagement = lazy(() => import("./pages/TasksManagement"));
const AdminUserTasksManager = lazy(() => import("./pages/AdminUserTasksManager"));
const AdvertisingDashboard = lazy(() => import("./pages/AdvertisingDashboard"));

// Create QueryClient instance outside of component to avoid recreating on every render
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const ScrollToTopWrapper = ({ children }: { children: React.ReactNode }) => {
  useScrollToTop();
  usePageViewTracking(); // Track PageView on every route change
  return <>{children}</>;
};

// Component to conditionally render LiveChatWidget - on homepage and subject pages
const ConditionalLiveChatWidget = () => {
  const location = useLocation();

  // Show on homepage and subject pages
  const allowedPaths = ["/", "/subjects/english", "/subjects/music", "/subjects/fitness", "/subjects/programming"];
  if (!allowedPaths.includes(location.pathname)) {
    return null;
  }

  return <LiveChatWidget />;
};

// Component to handle online status tracking and notifications
const OnlineStatusWrapper = ({ children }: { children: React.ReactNode }) => {
  useOnlineStatus({ enabled: true, inactivityTimeout: 30 * 60 * 1000 });
  useCommentReactionNotifications();
  return <>{children}</>;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <Router>
          <ScrollToTopWrapper>
            <AuthProvider>
              <OnlineStatusWrapper>
                <InactivityLogoutProvider timeoutMinutes={60} warningMinutes={2}>
                  <CurrencyProvider>
                    <ShoppingCartProvider>
                      <SidebarProvider>
                        <UnifiedConsentGuard>
                          <FrozenTutorGuard>
                            <div className="min-h-screen flex w-full">
                              <Suspense fallback={<LazyLoadingFallback />}>
                                <Routes>
                                  {/* Public routes */}
                                <Route path="/" element={<Home />} />
                                <Route path="/shop" element={<Shop />} />
                                <Route path="/featured" element={<Gallery />} />
                                {/* Redirect old gallery route to new featured route */}
                                <Route path="/gallery" element={<Navigate to="/featured" replace />} />
                                <Route path="/tutor-videos" element={<TutorVideosPage />} />
                                <Route path="/install" element={<InstallApp />} />
                                <Route path="/courses" element={<CoursesPage />} />
                                <Route path="/courses/:slug" element={<CourseDetailsPage />} />
                                <Route path="/subscription" element={<SubscriptionPage />} />
                                <Route path="/course-purchase-success" element={<CoursePurchaseSuccessPage />} />
                                <Route path="/my-courses" element={
                                  <ProtectedRoute>
                                    <MyCoursesPage />
                                  </ProtectedRoute>
                                } />
                                <Route path="/manage-subscription" element={
                                  <ProtectedRoute>
                                    <ManageSubscriptionPage />
                                  </ProtectedRoute>
                                } />
                                <Route path="/tutors" element={<Tutors />} />
                                <Route path="/online-tutors" element={<OnlineTutors />} />
                                <Route path="/online-fitness-tutors" element={<OnlineFitnessTutors />} />
                                <Route path="/announcements" element={<Announcements />} />
                                <Route path="/how-it-works" element={<HowItWorks />} />
                                <Route path="/subjects" element={<Subjects />} />
                                <Route path="/subjects/english" element={<EnglishSubject />} />
                                <Route path="/subjects/fitness" element={<FitnessSubject />} />
                                <Route path="/subjects/programming" element={<ProgrammingSubject />} />
                                <Route path="/subjects/travel" element={<TravelSubject />} />
                                <Route path="/subjects/music" element={<MusicSubject />} />
                                <Route path="/subjects/:subject/videos" element={<SubjectVideos />} />
                                <Route path="/countries" element={<Countries />} />
                                <Route
                                  path="/travel/country/:countryCode/articles/:articleId"
                                  element={<ArticleDetailPage />}
                                />
                                <Route path="/travel/country/:countryCode/articles" element={<CountryArticlesPage />} />
                                <Route path="/travel/country/us" element={<USCountryPage />} />
                                <Route path="/travel/country/nl" element={<NetherlandsCountryPage />} />
                                <Route path="/travel/country/hu" element={<HungaryCountryPage />} />
                                <Route path="/travel/country/eg" element={<EgyptCountryPage />} />
                                <Route path="/travel/country/it" element={<ItalyCountryPage />} />
                                <Route path="/travel/country/:countryCode" element={<DynamicCountryPage />} />
                                <Route path="/about" element={<About />} />
                                <Route path="/contact" element={<Contact />} />

                                {/* Support Pages */}
                                <Route path="/help-center" element={<HelpCenter />} />
                                <Route path="/safety" element={<Safety />} />
                                <Route path="/community-guidelines" element={<CommunityGuidelines />} />

                                {/* Company Pages */}
                                <Route path="/careers" element={<Careers />} />
                                <Route path="/blog" element={<Blog />} />
                                <Route path="/press" element={<Press />} />

                                {/* Legal Pages */}
                                <Route path="/terms-of-service" element={<TermsOfService />} />
                                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                                <Route path="/cookie-policy" element={<CookiePolicy />} />
                                <Route path="/refund-policy" element={<RefundPolicy />} />

                                <Route path="/notification-test" element={<NotificationTest />} />
                                <Route path="/tutor/:tutorId" element={<TutorDetails />} />
                                <Route path="/saved-tutors" element={<SavedTutors />} />
                                <Route path="/inspire-posts" element={<InspirePosts />} />
                                <Route path="/explore-lessons" element={<ExploreLessons />} />
                                <Route path="/auth" element={<Auth />} />
                                <Route path="/auth/confirm-password-reset/:token" element={<ResetPassword />} />
                                <Route path="/_/*" element={<RedirectHandler />} />
                <Route path="/language-adventure" element={<LanguageAdventure />} />
                <Route path="/egypt-marketing-video" element={<EgyptMarketingVideoPage />} />
                <Route path="/promo" element={<TutorNestaPromoPage />} />
                <Route
                                  path="/chat"
                                  element={
                                    <ProtectedRoute>
                                      <Chat />
                                    </ProtectedRoute>
                                  }
                                />

                                {/* Success Stories Route */}
                                <Route path="/success-stories" element={<SuccessStories />} />

                {/* Articles Routes */}
                <Route path="/articles" element={<Articles />} />
                <Route path="/articles/:id" element={<ArticleDetail />} />
                <Route 
                  path="/create-article" 
                  element={
                    <ProtectedRoute>
                      <CreateArticle />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/articles/edit/:id" 
                  element={
                    <ProtectedRoute>
                      <CreateArticle />
                    </ProtectedRoute>
                  } 
                />

                                {/* Travel Story Routes */}
                                <Route path="/travel-stories" element={<TravelStories />} />
                                <Route
                                  path="/travel-story/create"
                                  element={
                                    <ProtectedRoute>
                                      <TravelStoryForm />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/travel-story/edit/:id"
                                  element={
                                    <ProtectedRoute>
                                      <TravelStoryForm />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/my-travel-stories"
                                  element={
                                    <ProtectedRoute>
                                      <MyTravelStories />
                                    </ProtectedRoute>
                                  }
                                />

                                {/* Public Tutor Lessons - accessible without login since they're free */}
                                <Route path="/tutor-lessons" element={<TutorLessons />} />
                                <Route path="/english-lessons" element={<EnglishLessons />} />
                                <Route path="/fitness-lessons" element={<FitnessLessons />} />
                                <Route path="/programming-lessons" element={<ProgrammingLessons />} />
                                <Route path="/travel-lessons" element={<TravelLessons />} />
                                <Route path="/music-lessons" element={<MusicLessons />} />

                                {/* Tutor Chat Route */}
                                <Route
                                  path="/tutor-chats"
                                  element={
                                    <ProtectedTutorRoute>
                                      <DashboardLayout>
                                        <TutorChats />
                                      </DashboardLayout>
                                    </ProtectedTutorRoute>
                                  }
                                />

                                <Route
                                  path="/dashboard"
                                  element={
                                    <ProtectedRoute>
                                      <DashboardLayout>
                                        <Dashboard />
                                      </DashboardLayout>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin"
                                  element={
                                    <ProtectedRoute>
                                      <AdminDashboard />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/analytics"
                                  element={
                                    <ProtectedRoute>
                                      <DashboardLayout>
                                        <AdminAnalytics />
                                      </DashboardLayout>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/content"
                                  element={
                                    <ProtectedRoute>
                                      <DashboardLayout>
                                        <AdminContent />
                                      </DashboardLayout>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/gallery-showcase"
                                  element={
                                    <ProtectedRoute>
                                      <DashboardLayout>
                                        <GalleryShowcase />
                                      </DashboardLayout>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/gallery-management"
                                  element={
                                    <ProtectedRoute>
                                      <DashboardLayout>
                                        <AdminGalleryManagement />
                                      </DashboardLayout>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/review-articles"
                                  element={
                                    <ProtectedRoute>
                                      <DashboardLayout>
                                        <AdminReviewArticles />
                                      </DashboardLayout>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/download-content"
                                  element={
                                    <ProtectedRoute>
                                      <DashboardLayout>
                                        <AdminDownloadContent />
                                      </DashboardLayout>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/financials"
                                  element={
                                    <ProtectedRoute>
                                      <DashboardLayout>
                                        <AdminFinancials />
                                      </DashboardLayout>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/user-records"
                                  element={
                                    <ProtectedRoute>
                                      <AdminUserRecords />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/profile-suspensions"
                                  element={
                                    <ProtectedRoute>
                                      <DashboardLayout>
                                        <AdminProfileSuspensions />
                                      </DashboardLayout>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/fix-tutor-display"
                                  element={
                                    <ProtectedRoute>
                                      <AdminFixTutorDisplay />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/tutor-suspension"
                                  element={
                                    <ProtectedRoute>
                                      <DashboardLayout>
                                        <TutorSuspensionManager />
                                      </DashboardLayout>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/email"
                                  element={
                                    <ProtectedRoute>
                                      <AdminEmail />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/assistant"
                                  element={
                                    <ProtectedRoute>
                                      <DashboardLayout>
                                        <AssistantDashboard />
                                      </DashboardLayout>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/accountant"
                                  element={
                                    <ProtectedRoute>
                                      <DashboardLayout>
                                        <AccountantDashboard />
                                      </DashboardLayout>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/assistant/:id"
                                  element={
                                    <ProtectedRoute>
                                      <DashboardLayout>
                                        <AdminAssistantPayments />
                                      </DashboardLayout>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/assistant/payments/:id"
                                  element={
                                    <ProtectedRoute>
                                      <DashboardLayout>
                                        <AdminAssistantPayments />
                                      </DashboardLayout>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/payments"
                                  element={
                                    <ProtectedRoute>
                                      <AdminPaymentDashboard />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/tutor-requests"
                                  element={
                                    <ProtectedRoute>
                                      <TutorRequests />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/subject-requests"
                                  element={
                                    <ProtectedRoute>
                                      <DashboardLayout>
                                        <SubjectRequests />
                                      </DashboardLayout>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/chat-enhancements"
                                  element={
                                    <ProtectedRoute>
                                      <DashboardLayout>
                                        <ChatEnhancementsContent />
                                      </DashboardLayout>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/subjects"
                                  element={
                                    <ProtectedRoute>
                                      <DashboardLayout>
                                        <AdminSubjects />
                                      </DashboardLayout>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/announcements"
                                  element={
                                    <ProtectedRoute>
                                      <AdminAnnouncements />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/featured-tutors"
                                  element={
                                    <ProtectedRoute>
                                      <AdminFeaturedTutors />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/tutor-terms"
                                  element={
                                    <ProtectedRoute>
                                      <AdminTerms />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/payment-settings"
                                  element={
                                    <ProtectedRoute>
                                      <AdminPaymentSettings />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/email-notifications"
                                  element={
                                    <ProtectedRoute>
                                      <ManageEmailNotifications />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/payment-history/:tutorId"
                                  element={
                                    <ProtectedRoute>
                                      <AdminPaymentHistory />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/earnings-management"
                                  element={
                                    <ProtectedRoute>
                                      <AdminEarningsManagement />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/hero-image"
                                  element={
                                    <ProtectedRoute>
                                      <AdminHeroImage />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/homepage-section"
                                  element={
                                    <ProtectedRoute>
                                      <HomepageSectionAdmin />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/hero-tutor-cards"
                                  element={
                                    <ProtectedRoute>
                                      <AdminHeroTutorCards />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/testing"
                                  element={
                                    <ProtectedRoute>
                                      <DashboardLayout>
                                        <AdminTesting />
                                      </DashboardLayout>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/focus-pages"
                                  element={
                                    <ProtectedRoute>
                                      <DashboardLayout>
                                        <AdminFocusPages />
                                      </DashboardLayout>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/live-chat-agents"
                                  element={
                                    <ProtectedRoute>
                                      <DashboardLayout>
                                        <LiveChatAgents />
                                      </DashboardLayout>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/staff/tasks"
                                  element={
                                    <ProtectedRoute>
                                      <TasksManagement />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/user-tasks-manager"
                                  element={
                                    <ProtectedRoute>
                                      <AdminUserTasksManager />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/advertising"
                                  element={
                                    <ProtectedRoute>
                                      <AdvertisingDashboard />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/live-chat-support"
                                  element={
                                    <ProtectedRoute>
                                      <DashboardLayout>
                                        <LiveChatSupport />
                                      </DashboardLayout>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/live-chat-support"
                                  element={
                                    <ProtectedRoute>
                                      <DashboardLayout>
                                        <LiveChatSupport />
                                      </DashboardLayout>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/profile"
                                  element={
                                    <ProtectedRoute>
                                      <DashboardLayout>
                                        <ProfileRouter />
                                      </DashboardLayout>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/profile/:id"
                                  element={
                                    <ProtectedRoute>
                                      <DashboardLayout>
                                        <ProfileRouter />
                                      </DashboardLayout>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/schedule"
                                  element={
                                    <ProtectedRoute>
                                      <Schedule />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/students"
                                  element={
                                    <ProtectedRoute>
                                      <Students />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/lessons"
                                  element={
                                    <ProtectedRoute>
                                      <Lessons />
                                    </ProtectedRoute>
                                  }
                                />
                <Route path="/reschedule-approval/:token" element={<RescheduleApproval />} />
                                <Route
                                  path="/admin/payment-requests"
                                  element={
                                    <ProtectedRoute>
                                      <AdminPaymentRequests />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/payment-requests"
                                  element={
                                    <ProtectedRoute>
                                      <PaymentRequests />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/payment-request-success"
                                  element={
                                    <ProtectedRoute>
                                      <PaymentRequestSuccess />
                                    </ProtectedRoute>
                                  }
                                />
                <Route
                  path="/lesson/:lessonId"
                  element={<LessonDetail />}
                />
                                <Route
                                  path="/lesson/:lessonId/edit"
                                  element={
                                    <ProtectedRoute>
                                      <LessonEdit />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/messages"
                                  element={
                                    <ProtectedRoute>
                                      <Messages />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/messages/support"
                                  element={
                                    <ProtectedRoute>
                                      <SupportMessages />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/file-preview/:messageId"
                                  element={
                                    <ProtectedRoute>
                                      <FilePreview />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/earnings"
                                  element={
                                    <ProtectedRoute>
                                      <Earnings />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/settings"
                                  element={
                                    <ProtectedRoute>
                                      <AppSettings />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/booking-confirmation"
                                  element={
                                    <ProtectedRoute>
                                      <BookingConfirmation />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/payment-success"
                                  element={
                                    <PaymentSuccess />
                                  }
                                />
                                <Route
                                  path="/classroom"
                                  element={
                                    <ProtectedRoute>
                                      <Classroom />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/video-call/:bookingId"
                                  element={
                                    <ProtectedRoute>
                                      <VideoCall />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/tutors"
                                  element={
                                    <ProtectedRoute>
                                      <DashboardLayout>
                                        <AdminTutors />
                                      </DashboardLayout>
                                    </ProtectedRoute>
                                  }
                                />
                                {/* Tutor Evaluation Route */}
                                <Route
                                  path="/admin/tutor-evaluation"
                                  element={
                                    <ProtectedRoute>
                                      <AdminTutorEvaluation />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/students"
                                  element={
                                    <ProtectedRoute>
                                      <DashboardLayout>
                                        <AdminStudents />
                                      </DashboardLayout>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/support-contacts"
                                  element={
                                    <ProtectedRoute>
                                      <AdminSupportContacts />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/user-management"
                                  element={
                                    <ProtectedRoute>
                                      <DashboardLayout>
                                        <AdminUserManagement />
                                      </DashboardLayout>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/users"
                                  element={
                                    <ProtectedRoute>
                                      <DashboardLayout>
                                        <AdminUserManagement />
                                      </DashboardLayout>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin-bookings"
                                  element={
                                    <ProtectedRoute>
                                      <DashboardLayout>
                                        <AdminBookings />
                                      </DashboardLayout>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin-lessons"
                                  element={
                                    <ProtectedRoute>
                                      <DashboardLayout>
                                        <AdminLessons />
                                      </DashboardLayout>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/subject-management"
                                  element={
                                    <ProtectedRoute>
                                      <DashboardLayout>
                                        <AdminSubjectManagement />
                                      </DashboardLayout>
                                    </ProtectedRoute>
                                  }
                                />
                                {/* Admin Courses Routes */}
                                <Route
                                  path="/admin/courses"
                                  element={
                                    <ProtectedRoute>
                                      <DashboardLayout>
                                        <AdminCourses />
                                      </DashboardLayout>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/courses/new"
                                  element={
                                    <ProtectedRoute>
                                      <AdminCourseForm />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/courses/:id/edit"
                                  element={
                                    <ProtectedRoute>
                                      <AdminCourseForm />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/become-local-fitness-guide"
                                  element={
                                    <ProtectedRoute>
                                      <BecomeLocalFitnessGuide />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/become-local-tutor"
                                  element={
                                    <ProtectedRoute>
                                      <BecomeLocalTutor />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/become-local-music-guide"
                                  element={
                                    <ProtectedRoute>
                                      <BecomeLocalMusicGuide />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/become-local-travel-guide"
                                  element={
                                    <ProtectedRoute>
                                      <BecomeLocalTravelGuide />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route path="/local-fitness-coaches" element={<LocalFitnessCoaches />} />
                                <Route path="/local-travel-guides" element={<LocalTravelGuides />} />
                                <Route path="/local-music-coaches" element={<LocalMusicCoaches />} />
                                <Route path="/local-travel-coaches" element={<LocalTravelGuides />} />
                                <Route path="/local-coach/:id" element={<LocalCoachDetails />} />
                                <Route
                                  path="/admin/local-coaches"
                                  element={
                                    <ProtectedRoute>
                                      <AdminLocalCoaches />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/travel-stories"
                                  element={
                                    <ProtectedRoute>
                                      <DashboardLayout>
                                        <AdminTravelStories />
                                      </DashboardLayout>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/travel-stories-order"
                                  element={
                                    <ProtectedRoute>
                                      <AdminTravelStoriesOrder />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/inspire-posts-order"
                                  element={
                                    <ProtectedRoute>
                                      <AdminInspirePostsOrder />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/travel-management"
                                  element={
                                    <ProtectedRoute>
                                      <DashboardLayout>
                                        <AdminTravelManagement />
                                      </DashboardLayout>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/travel-pages"
                                  element={
                                    <ProtectedRoute>
                                      <DashboardLayout>
                                        <AdminTravelPages />
                                      </DashboardLayout>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/hero-guide-settings"
                                  element={
                                    <ProtectedRoute>
                                      <ProtectedTutorRoute>
                                        <HeroGuideSettings />
                                      </ProtectedTutorRoute>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/shop"
                                  element={
                                    <ProtectedRoute>
                                      <DashboardLayout>
                                        <AdminShop />
                                      </DashboardLayout>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/tracking-events"
                                  element={
                                    <ProtectedRoute>
                                      <TrackingEvents />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/discounts"
                                  element={
                                    <ProtectedRoute>
                                      <AdminDiscounts />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/admin/discount-codes"
                                  element={
                                    <ProtectedRoute>
                                      <AdminDiscountCodes />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route path="*" element={<NotFound />} />
                              </Routes>
                              </Suspense>

                              {/* Add Live Chat Widget conditionally */}
                              <ConditionalLiveChatWidget />
                              <CartSidebar />
                            </div>
                          </FrozenTutorGuard>
                        </UnifiedConsentGuard>
                      </SidebarProvider>
                    </ShoppingCartProvider>
                  </CurrencyProvider>
                </InactivityLogoutProvider>
              </OnlineStatusWrapper>
            </AuthProvider>
          </ScrollToTopWrapper>
        </Router>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
